import type { JSX } from "react";
import { StepCard } from "./components";

/**
 * P2d-2 create conversation (design spec §02, agents-02 pattern): the admin's
 * description becomes a user bubble; the bounded generation's outcome renders
 * as a plan line + generative step cards that light up one at a time —
 * confirmed cards collapse to green facts. Controlled: all state and the
 * generation/publish calls live in ProfilesSection (P2a pipeline unchanged).
 *
 * Honesty rule (2026-07-06 review lesson): NOTHING on these cards is a
 * hardcoded claim about live systems — the connector card renders the real
 * evidence inventory, so the copy can never contradict the data again.
 */

/** Tenant connector inventory line (from the evidence endpoint). */
export interface ConnectorLine {
  id: string;
  label: string;
  state: "connected" | "expired" | "unknown";
}

export function CreateFlow(props: {
  description: string;
  onDescription: (v: string) => void;
  busy: boolean;
  error: string | null;
  /** null until the bounded generation produced a layer. */
  generated: {
    name: string;
    id: string;
    tagline: string;
    skills: string[];
    wont: string[];
  } | null;
  onGenerate: () => void;
  /** 说话改卡: regenerate the UNCONFIRMED cards with a refinement sentence. */
  onRefine: (text: string) => void;
  step: number;
  onStep: (n: number) => void;
  onName: (v: string) => void;
  onId: (v: string) => void;
  onTagline: (v: string) => void;
  skillUniverse: string[];
  onToggleSkill: (id: string) => void;
  skillName: (id: string) => string;
  /** P2d-4c: open the shared skill library IN PLACE (no page hop). */
  onOpenSkills: () => void;
  connectors: ConnectorLine[] | null;
  /** P2e real selection: bound connector ids carried into the publish. */
  selectedConnectors: string[];
  onToggleConnector: (id: string) => void;
  /** STEP 4 (real scheduling): the NL phrase + its parsed cadence label. */
  scheduleText: string;
  onScheduleText: (v: string) => void;
  scheduleParsed: string | null;
  onPublish: () => void;
  onCancel: () => void;
}): JSX.Element {
  const g = props.generated;
  const phase = (n: number): "done" | "active" | "next" =>
    props.step > n ? "done" : props.step === n ? "active" : "next";
  const next = (): void => props.onStep(props.step + 1);

  return (
    <div className="bd-create bd-scope" data-testid="profiles-create">
      <div className="profiles-form-head">
        <h3>新建助手</h3>
        <button onClick={props.onCancel}>取消</button>
      </div>

      {/* The admin's ask, echoed as the conversation opener once sent. */}
      {g === null ? (
        <div>
          <p className="cc-desc">描述它要做什么，剩下的我来搭——搭出来的每一步都可以改</p>
          <textarea
            rows={3}
            value={props.description}
            placeholder="比如：给售后团队建一个答工单和产品问题的助手"
            onChange={(e) => props.onDescription(e.target.value)}
          />
          <div className="bd-step-actions">
            <button
              className="primary"
              disabled={props.busy || props.description.trim().length === 0}
              onClick={props.onGenerate}
            >
              {props.busy ? "搭建中…" : "开始搭建"}
            </button>
          </div>
          {props.busy && <div className="bd-thinking">正在理解需求 · 对照本租户已注册的连接器与技能</div>}
        </div>
      ) : (
        <div>
          <div className="bd-msg-user">{props.description}</div>
          <div className="bd-msg-a">
            好的，按你的描述我会这样搭——每一步都可以直接改，也可以在下面继续跟我说：
            <span className="bd-plan-chips">
              <span className="bd-chip">✓ {g.name}</span>
              {g.skills.map((s) => (
                <span key={s} className="bd-chip">
                  ✓ {props.skillName(s)}
                </span>
              ))}
              {g.wont.slice(0, 2).map((w) => (
                <span key={w} className="bd-chip">
                  ✕ {w.slice(0, 14)}
                </span>
              ))}
            </span>
          </div>

          <StepCard
            step={1}
            total={4}
            title="给它起名"
            hint="三项都可以改；标识用在系统内部，发布后就固定了"
            phase={phase(1)}
            fact={`名称「${props.generated?.name ?? ""}」· 标识 ${props.generated?.id ?? ""}`}
            onReopen={() => props.onStep(1)}
          >
            <label className="bd-lbl">
              名称（用户看到的称呼）
              <input value={g.name} onChange={(e) => props.onName(e.target.value)} />
            </label>
            <label className="bd-lbl">
              标识（小写英文，创建后不可改）
              <input value={g.id} onChange={(e) => props.onId(e.target.value)} />
            </label>
            <label className="bd-lbl">
              简介（一句话，18 字内）
              <input value={g.tagline} onChange={(e) => props.onTagline(e.target.value)} />
            </label>
            <div className="bd-step-actions">
              <button
                className="primary"
                disabled={g.name.trim().length === 0 || g.id.trim().length === 0}
                onClick={next}
              >
                就这么叫
              </button>
            </div>
          </StepCard>

          <StepCard
            step={2}
            total={4}
            title="选它能用的连接器"
            hint="绑定 git 连接器=它的源码问答只读这些仓库；绑定飞书=它建的定时任务默认用它。不选=沿用全局默认"
            phase={phase(2)}
            fact={
              props.selectedConnectors.length > 0
                ? `连接器 ${props.selectedConnectors.length} 个已绑定`
                : "不绑定连接器 · 沿用全局默认"
            }
            onReopen={() => props.onStep(2)}
          >
            {props.connectors === null ? (
              <div className="bd-thinking">正在读取连接器清单</div>
            ) : props.connectors.length === 0 ? (
              <p className="cc-desc">本租户还没有配置连接器——可以先在「系统 · 集成」页添加</p>
            ) : (
              <div className="bd-conn-inventory">
                {props.connectors.map((c) => (
                  <label key={c.id} className="bd-conn-line" style={{ cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={props.selectedConnectors.includes(c.id)}
                      onChange={() => props.onToggleConnector(c.id)}
                    />
                    <span
                      className={`bd-dot bd-dot-${c.state === "connected" ? "ok" : c.state === "expired" ? "warn" : "off"}`}
                    />
                    <span className="bd-conn-name">{c.label}</span>
                    <span className="bd-conn-state">
                      {c.state === "connected" ? "已连接" : c.state === "expired" ? "凭证过期" : "状态未知"}
                    </span>
                  </label>
                ))}
              </div>
            )}
            <div className="bd-step-actions" style={{ marginTop: "var(--space-2)" }}>
              <button className="primary" onClick={next}>
                {props.selectedConnectors.length > 0 ? "就这些，下一步" : "先不绑，下一步"}
              </button>
            </div>
          </StepCard>

          <StepCard
            step={3}
            total={4}
            title="选它的技能"
            hint="内置能力 + 本租户已上传的外置技能都在这里；越权项在搭建时就已被拦下"
            phase={phase(3)}
            fact={`技能 ${g.skills.length} 项：${g.skills.map(props.skillName).join("、")}`}
            onReopen={() => props.onStep(3)}
          >
            <div className="bd-plan-chips">
              {props.skillUniverse.map((s) => (
                <label key={s} className="bd-chip" title={s}>
                  <input
                    type="checkbox"
                    checked={g.skills.includes(s)}
                    onChange={() => props.onToggleSkill(s)}
                  />
                  {props.skillName(s)}
                </label>
              ))}
            </div>
            <p className="bd-step-note">
              要的技能不在里面？
              <button className="bd-chip" data-testid="bd-open-skills" onClick={props.onOpenSkills}>
                打开技能库上传
              </button>
              ——就地上传，传完这里立刻能选。
            </p>
            <div className="bd-step-actions" style={{ marginTop: "var(--space-2)" }}>
              <button className="primary" disabled={g.skills.length === 0} onClick={next}>
                就这些
              </button>
            </div>
          </StepCard>

          <StepCard
            step={4}
            total={4}
            title="什么时候自动跑"
            hint="用一句话描述节奏和要做的事，发布时会真的建一条定时任务（日程页可改可停）；不需要就跳过"
            phase={phase(4)}
            fact={
              props.scheduleParsed !== null
                ? `定时：${props.scheduleParsed} · 发布时创建`
                : "定时：暂不设置"
            }
            onReopen={() => props.onStep(4)}
          >
            <label className="bd-lbl">
              节奏 + 要做的事（比如「每天早上 9 点，把昨天的学员问题汇总一下」）
              <input
                value={props.scheduleText}
                onChange={(e) => props.onScheduleText(e.target.value)}
              />
            </label>
            {props.scheduleText.trim().length > 0 && (
              <p className="bd-step-note">
                {props.scheduleParsed !== null
                  ? `听懂了：${props.scheduleParsed}`
                  : "没听懂这个节奏——换个说法，比如「每个工作日早上 9 点」「每周一 10:30」"}
              </p>
            )}
            <div className="bd-step-actions">
              <button onClick={() => { props.onScheduleText(""); next(); }}>先不设</button>
              <button
                className="primary"
                disabled={props.scheduleText.trim().length > 0 && props.scheduleParsed === null}
                onClick={next}
              >
                {props.scheduleText.trim().length > 0 ? "就这么定" : "下一步"}
              </button>
            </div>
          </StepCard>

          {props.step >= 5 && (
            <div className="bd-step" data-testid="bd-create-confirm">
              <div className="bd-step-lbl">发布确认</div>
              <div className="bd-step-title">
                {g.name} · 版本 1 · 新对话起生效
              </div>
              <div className="bd-step-hint">
                发布后到「助手绑定与运行状况」把它绑定给企业，用户才会遇到它
                {props.scheduleParsed !== null
                  ? `；同时会创建定时任务（${props.scheduleParsed}），在「日程」页可管理`
                  : ""}
              </div>
              <div className="bd-step-actions">
                <button onClick={() => props.onStep(1)}>返回修改</button>
                <button className="primary" disabled={props.busy} onClick={props.onPublish}>
                  {props.busy ? "发布中…" : "确认发布"}
                </button>
              </div>
            </div>
          )}

          {/* Reply-or-refine: regenerates the UNCONFIRMED cards only (spec §02). */}
          <RefineComposer busy={props.busy} onRefine={props.onRefine} />
        </div>
      )}
      {props.error !== null && <p className="bd-error">{props.error}</p>}
    </div>
  );
}

/** The persistent composer under the step cards (uncontrolled input). */
function RefineComposer(props: { busy: boolean; onRefine: (text: string) => void }): JSX.Element {
  return (
    <div className="bd-composer">
      <textarea
        rows={1}
        placeholder="继续说，或直接点卡片修改——比如「名字里带上团队名」"
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            const el = e.currentTarget;
            if (el.value.trim().length > 0 && !props.busy) {
              props.onRefine(el.value);
              el.value = "";
            }
          }
        }}
      />
      <button
        disabled={props.busy}
        onClick={(e) => {
          const el = e.currentTarget.previousElementSibling as HTMLTextAreaElement | null;
          if (el !== null && el.value.trim().length > 0) {
            props.onRefine(el.value);
            el.value = "";
          }
        }}
      >
        发送
      </button>
    </div>
  );
}
